import { useState, useEffect } from "react";
import { useApp } from "../store.jsx";
import { fmtMonth } from "../calc.js";
import GateDeps from "./GateDeps.jsx";
import TaskDeps from "./TaskDeps.jsx";

const FTE_STEPS = [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3];
const PRIORITIES = [["P1","Quick Win"],["P2","High"],["P3","Medium"],["P4","Long-term"]];

// Text input that holds its own draft and commits (patches) on blur, so we
// don't fire a PUT on every keystroke.
function TextField({ label, value, onCommit, multiline, hint, rows = 3 }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const commit = () => { if (v !== (value ?? "")) onCommit(v); };
  return (
    <div className="field">
      <span className="label">{label}</span>
      {multiline
        ? <textarea rows={rows} value={v} onChange={e => setV(e.target.value)} onBlur={commit}/>
        : <input type="text" value={v} onChange={e => setV(e.target.value)} onBlur={commit}/>}
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

// One item per line ↔ string array. Commits on blur.
function ListField({ label, value, onCommit, hint }) {
  const text = (Array.isArray(value) ? value : []).join("\n");
  const [v, setV] = useState(text);
  useEffect(() => { setV(text); }, [text]);
  const commit = () => {
    const arr = v.split("\n").map(s => s.trim()).filter(Boolean);
    if (JSON.stringify(arr) !== JSON.stringify(value || [])) onCommit(arr);
  };
  return (
    <div className="field">
      <span className="label">{label}</span>
      <textarea rows={Math.max(3, (Array.isArray(value) ? value.length : 0) + 1)}
                value={v} onChange={e => setV(e.target.value)} onBlur={commit}/>
      <span className="hint">{hint || "One per line."}</span>
    </div>
  );
}

// Repeater for phases: array of { name, description }. Rows live in local state
// so a freshly-added blank row sticks around (the backend drops empty phases);
// edits commit the cleaned array on blur.
function PhasesField({ value, onCommit }) {
  const [rows, setRows] = useState(Array.isArray(value) ? value : []);
  useEffect(() => { setRows(Array.isArray(value) ? value : []); }, [value]);

  const commit = (next) => {
    const cleaned = next
      .map(p => ({ name: (p.name || "").trim(), description: (p.description || "").trim() }))
      .filter(p => p.name || p.description);
    if (JSON.stringify(cleaned) !== JSON.stringify(value || [])) onCommit(cleaned);
  };
  const setField = (i, key, val) =>
    setRows(rows.map((p, j) => j === i ? { ...p, [key]: val } : p));
  const add    = () => setRows([...rows, { name: "", description: "" }]);   // local only
  const remove = (i) => { const next = rows.filter((_, j) => j !== i); setRows(next); commit(next); };

  return (
    <div className="field">
      <span className="label">Implementation phases</span>
      {rows.map((p, i) => (
        <div className="phase-row" key={i}>
          <input type="text" placeholder="Phase name" value={p.name || ""}
                 onChange={e => setField(i, "name", e.target.value)} onBlur={() => commit(rows)}/>
          <input type="text" placeholder="Description" value={p.description || ""}
                 onChange={e => setField(i, "description", e.target.value)} onBlur={() => commit(rows)}/>
          <button className="btn danger" type="button" onClick={() => remove(i)}>✕</button>
        </div>
      ))}
      <div><button className="btn" type="button" onClick={add} style={{marginTop:6}}>+ Add phase</button></div>
    </div>
  );
}

function Range({ value, min, max, step, onChange, format }) {
  return (
    <div className="editor-grid">
      <input type="range" value={value} min={min} max={max} step={step}
             onChange={e => onChange(Number(e.target.value))}/>
      <span className="val">{format ? format(value) : value}</span>
    </div>
  );
}

export default function TaskEditor({ task }) {
  const { patchTask, resetTask, archiveTask, duplicateTask, settings } = useApp();
  // View-only by default. Clicking "Edit" enables the form controls so an
  // accidental scroll/click can't change a saved value.
  const [editing, setEditing] = useState(false);

  // Progress-update modal. Opens when the user releases the slider on a value
  // different from the saved percentDone. `sliderDraft` mirrors the slider's
  // displayed position during a drag so it can be reverted on Cancel.
  const [sliderDraft, setSliderDraft] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingPercent, setPendingPercent] = useState(0);
  const [pendingComment, setPendingComment] = useState("");

  if (!task) return null;
  const p = (patch) => patchTask(task.id, patch);
  const savedPercent = Number(task.percentDone) || 0;
  const shownPercent = sliderDraft ?? savedPercent;

  const isActive = task.active !== false;

  const onArchive = () => {
    if (!window.confirm("Hide this task from the plan? It's recoverable from the Archived panel on the department page.")) return;
    archiveTask(task.id);
    window.location.hash = "#/dept/" + task.deptShort;
  };

  const onDuplicate = async () => {
    const created = await duplicateTask(task.id);
    if (created) window.location.hash = "#/task/" + created.id;
  };

  // Slider release: if the dragged value differs from the saved one, open the
  // modal so the user can attach a comment to the change. Otherwise just drop
  // the local draft.
  const onSliderRelease = () => {
    if (sliderDraft === null) return;
    if (sliderDraft === savedPercent) { setSliderDraft(null); return; }
    setPendingPercent(sliderDraft);
    setPendingComment("");
    setModalOpen(true);
  };

  const saveProgress = () => {
    const patch = { percentDone: pendingPercent };
    const trimmed = pendingComment.trim();
    if (trimmed) patch.progressComment = trimmed;
    p(patch);
    setModalOpen(false);
    setSliderDraft(null);
  };

  const cancelProgress = () => {
    setModalOpen(false);
    setSliderDraft(null); // visually reverts slider to savedPercent
  };

  const history = Array.isArray(task.progressHistory) ? task.progressHistory : [];

  const scoreTooltip = (key) => {
    if (!settings) return undefined;
    if (key === "Q") return settings.descQuality;
    if (key === "S") return settings.descStrategic;
    if (key === "C") return settings.descCustomer;
    return undefined;
  };

  return (
    <section className="brief">
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:6}}>
        <h2 style={{margin:0}}>{editing ? "Edit brief content" : "Brief content"}</h2>
        <button className={"btn " + (editing ? "primary" : "")}
                type="button"
                onClick={() => setEditing(v => !v)}>
          {editing ? "Done" : "Edit"}
        </button>
      </div>
      <p style={{color:"var(--muted)",marginTop:0,fontSize:13}}>
        {editing
          ? "Editing enabled. Title, summary, dependencies and gate links save when you click away."
          : "Brief content and dependency links are read-only. Click Edit to change them. The Customize panel below is always live."}
      </p>

      <fieldset disabled={!editing} style={{
        border:0, padding:0, margin:0,
        opacity: editing ? 1 : 0.85,
      }}>
      <div className="details-editor">
        <TextField label="Title" value={task.title} onCommit={v => p({ title: v })}/>

        <div className="field">
          <span className="label">Priority</span>
          <select value={task.priority || "P3"} onChange={e => p({ priority: e.target.value })}>
            {PRIORITIES.map(([code, lab]) => <option key={code} value={code}>{code} — {lab}</option>)}
          </select>
        </div>

        <TextField label="Owner" value={task.owner} onCommit={v => p({ owner: v })}/>
        <TextField label="Summary" value={task.summary} onCommit={v => p({ summary: v })} multiline rows={4}/>
        <ListField label="Potential gains" value={task.gains} onCommit={v => p({ gains: v })}/>
        <ListField label="Steps needed" value={task.steps} onCommit={v => p({ steps: v })}/>
        <ListField label="Expected outcome" value={task.outcome} onCommit={v => p({ outcome: v })}/>
        <PhasesField value={task.phases} onCommit={v => p({ phases: v })}/>
        <TextField label="Effort headline"
                   value={(task.effortJson || {})["Effort headline"] || ""}
                   onCommit={v => p({ effortJson: { ...(task.effortJson || {}), "Effort headline": v } })}/>
        <TextField label="Comment" multiline rows={4}
                   value={(task.effortJson || {})["Comment"] || ""}
                   onCommit={v => p({ effortJson: { ...(task.effortJson || {}), "Comment": v } })}/>
        <TextField label="Dependencies" value={task.dependencies} onCommit={v => p({ dependencies: v })} multiline/>
        <TextField label="Key risks & mitigations" value={task.risks} onCommit={v => p({ risks: v })} multiline/>
      </div>
      </fieldset>

      <div className="divider"/>
      <h2>Customize</h2>
      <p style={{color:"var(--muted)",marginTop:0,fontSize:13}}>
        Adjust this task's effort, resource allocation, start date and impact. The Gantt and the
        financial impact card below recompute live.
      </p>

      <label className="active-toggle" style={{
        display:"inline-flex", alignItems:"center", gap:8,
        padding:"6px 10px", border:"1px solid var(--grid)", borderRadius:8,
        background: isActive ? "transparent" : "rgba(127,29,29,0.18)",
        color: isActive ? "var(--ink)" : "#fca5a5",
        marginBottom:12, cursor:"pointer", fontSize:13,
      }}>
        <input type="checkbox" checked={isActive}
               onChange={e => p({ active: e.target.checked })}
               style={{accentColor:"var(--link)"}}/>
        <span><b>Active</b> — {isActive
          ? "included in portfolio totals"
          : "excluded from totals; still shown on the Gantt (dimmed)"}</span>
      </label>

      <div className="editor-grid">
        <span className="label">Status</span>
        <select value={task.status || "Planned"}
                onChange={e => p({ status: e.target.value })}>
          <option value="Future">Future</option>
          <option value="Planned">Planned</option>
          <option value="Building">Building</option>
          <option value="Live">Live</option>
        </select>
        <span className="val">{task.status || "Planned"}</span>

        <span className="label">Progress (% done)</span>
        <input type="range" min="0" max="100" step="5"
               value={shownPercent}
               onChange={e => setSliderDraft(Number(e.target.value))}
               onMouseUp={onSliderRelease}
               onTouchEnd={onSliderRelease}
               onKeyUp={onSliderRelease}/>
        <span className="val">{shownPercent}%</span>

        <span className="label">Effort (person-weeks)</span>
        <input type="number" step="0.5" min="0" value={Number(task.effortPw).toFixed(1)}
               onChange={e => p({ effortPw: Number(e.target.value) })}/>
        <span className="val">{Number(task.effortPw).toFixed(1)} pw</span>

        <span className="label">FTE allocation</span>
        <select value={String(task.fteAllocation)}
                onChange={e => p({ fteAllocation: Number(e.target.value) })}>
          {FTE_STEPS.map(s => (
            <option key={s} value={s}>{s.toFixed(2)} persons</option>
          ))}
        </select>
        <span className="val">{Number(task.fteAllocation).toFixed(2)} FTE</span>

        <span className="label">Start month (0–19)</span>
        <input type="range" min="0" max="19" step="0.5" value={task.startMonth}
               onChange={e => p({ startMonth: Number(e.target.value) })}/>
        <span className="val">{fmtMonth(task.startMonth)}</span>

        <span className="label">Total used hours today (h/mo)</span>
        <input type="number" step="1" min="0" value={Number(task.totalUsedHoursToday) || 0}
               onChange={e => p({ totalUsedHoursToday: Number(e.target.value) })}/>
        <span className="val">{Number(task.totalUsedHoursToday) || 0} h</span>

        <span className="label">Monthly hours saved (h/mo)</span>
        <input type="number" step="1" min="0" value={Number(task.monthlyHoursSaved)}
               onChange={e => p({ monthlyHoursSaved: Number(e.target.value) })}/>
        <span className="val">{Number(task.monthlyHoursSaved)} h</span>

        <span className="label" title="Monthly hours saved ÷ Total used hours today">Expected saved</span>
        <span className="val" style={{textAlign:"left", color:"var(--muted)"}}>
          {Number(task.totalUsedHoursToday) > 0
            ? `${((Number(task.monthlyHoursSaved) || 0) / Number(task.totalUsedHoursToday) * 100).toFixed(0)} %`
            : "—"}
        </span>
        <span className="val">derived</span>

        <span className={"label " + (scoreTooltip("Q") ? "tt" : "")}
              style={scoreTooltip("Q") ? { textDecoration:"underline dotted" } : undefined}>
          Quality score (0–5)
          {scoreTooltip("Q") && <span className="tt-text">{scoreTooltip("Q")}</span>}
        </span>
        <input type="range" min="0" max="5" step="1" value={task.scoreQuality}
               onChange={e => p({ scoreQuality: Number(e.target.value) })}/>
        <span className="val">{task.scoreQuality} / 5</span>

        <span className={"label " + (scoreTooltip("S") ? "tt" : "")}
              style={scoreTooltip("S") ? { textDecoration:"underline dotted" } : undefined}>
          Importance score (0–5)
          {scoreTooltip("S") && <span className="tt-text">{scoreTooltip("S")}</span>}
        </span>
        <input type="range" min="0" max="5" step="1" value={task.scoreStrategic}
               onChange={e => p({ scoreStrategic: Number(e.target.value) })}/>
        <span className="val">{task.scoreStrategic} / 5</span>

        <span className={"label " + (scoreTooltip("C") ? "tt" : "")}
              style={scoreTooltip("C") ? { textDecoration:"underline dotted" } : undefined}>
          Customer score (0–5)
          {scoreTooltip("C") && <span className="tt-text">{scoreTooltip("C")}</span>}
        </span>
        <input type="range" min="0" max="5" step="1" value={task.scoreCustomer}
               onChange={e => p({ scoreCustomer: Number(e.target.value) })}/>
        <span className="val">{task.scoreCustomer} / 5</span>

        <span className="label">Net new revenue (SEK/mo)</span>
        <input type="number" step="1000" min="0" value={Number(task.newRevenueSek)}
               onChange={e => p({ newRevenueSek: Number(e.target.value) })}/>
        <span className="val">{Number(task.newRevenueSek).toLocaleString("sv-SE")} SEK</span>

        <span className="label">Tools / licenses needed</span>
        <input type="text" value={task.toolsNeeded || ""}
               placeholder="e.g. Gemini Enterprise (existing)"
               onChange={e => p({ toolsNeeded: e.target.value })}/>
        <span className="val">{task.toolsNeeded ? "" : "—"}</span>
      </div>

      {history.length > 0 && (
        <>
        <div className="divider"/>
        <h3 style={{marginBottom:6}}>Progress history</h3>
        <ul className="progress-history" style={{listStyle:"none", padding:0, marginTop:12}}>
          {[...history].reverse().map((h, i) => (
            <li key={i} style={{
              padding:"6px 0",
              borderTop: i === 0 ? "none" : "1px solid var(--grid)",
            }}>
              <div style={{display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--muted)"}}>
                <span>
                  {h.from === h.to
                    ? `Comment at ${h.to}%`
                    : `${h.from}% → ${h.to}%`}
                </span>
                <span>{new Date(h.at).toLocaleString("sv-SE")}</span>
              </div>
              {h.comment ? <div style={{marginTop:2}}>{h.comment}</div> : null}
            </li>
          ))}
        </ul>
        </>
      )}

      <fieldset disabled={!editing} style={{
        border:0, padding:0, margin:0,
        opacity: editing ? 1 : 0.85,
      }}>
        <div className="divider"/>
        <TaskDeps task={task}/>

        <div className="divider"/>
        <GateDeps task={task}/>
      </fieldset>

      <div className="divider"/>
      <div className="row-actions">
        {editing && (
          <button className="btn" onClick={() => resetTask(task.id)}>Reset parameters</button>
        )}
        <button className="btn" onClick={onDuplicate}>Copy task</button>
        {editing && (
          <button className="btn danger" onClick={onArchive}>Archive task</button>
        )}
      </div>

      {modalOpen && (
        <div onClick={cancelProgress}
             style={{
               position:"fixed", inset:0, background:"rgba(0,0,0,0.55)",
               display:"flex", alignItems:"center", justifyContent:"center",
               zIndex:1000,
             }}>
          <div onClick={e => e.stopPropagation()}
               style={{
                 background:"var(--card, #0f172a)", color:"var(--ink, #e2e8f0)",
                 border:"1px solid var(--grid, #334155)", borderRadius:10,
                 padding:18, width:"min(480px, 92vw)",
                 boxShadow:"0 12px 32px rgba(0,0,0,0.4)",
               }}>
            <h3 style={{margin:"0 0 6px"}}>Update progress</h3>
            <p style={{margin:"0 0 12px", color:"var(--muted, #94a3b8)", fontSize:13}}>
              {savedPercent}% → <b style={{color:"#fff"}}>{pendingPercent}%</b>. Add a note (optional).
            </p>
            <textarea rows={3} value={pendingComment} autoFocus
                      placeholder="e.g. Phase 1 shipped to beta users."
                      onChange={e => setPendingComment(e.target.value)}
                      style={{
                        width:"100%", boxSizing:"border-box",
                        background:"transparent", color:"inherit",
                        border:"1px solid var(--grid, #334155)", borderRadius:6,
                        padding:8, fontFamily:"inherit", fontSize:14,
                        resize:"vertical",
                      }}/>
            <div className="row-actions" style={{marginTop:12, justifyContent:"flex-end"}}>
              <button className="btn" type="button" onClick={cancelProgress}>Cancel</button>
              <button className="btn primary" type="button" onClick={saveProgress}>Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
